import { Button } from '@/app/common/components/button/Button'
import PageLoader from '@/app/common/components/PageLoader'
import { parseRequestError } from '@/app/common/utilities'
import { currencyFormatter } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Award, Eye } from 'lucide-react'
import { Purse } from '../../schema'
import { DistributionPlanSchema } from './schema'
import { getDistributionPlanPreview } from './USE_WITH_CAUTION.api'
import { useDownloadDistributionPlanPreviewReport } from '@/queries/program_purses/useDownloadDistributionPlanPreviewReport'
import { SubmitButton } from '@/app/common/components/SubmitButton'

interface DistributionPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  purse: Purse
  programId: number
  onProceedToAward: () => void
}

export function DistributionPreviewModal({
  open,
  onOpenChange,
  purse,
  programId,
  onProceedToAward,
}: DistributionPreviewModalProps) {
  const { toast } = useToast()

  const previewQuery = useQuery({
    queryKey: ['distribution-preview', programId, purse.id],
    staleTime: 0,
    queryFn: async () => {
      try {
        const response = await getDistributionPlanPreview({
          program_id: programId,
          program_purse_id: purse.id,
          distribution_strategy: 'single',
        })

        const parseResult = DistributionPlanSchema.safeParse(response)

        if (!parseResult.success) {
          console.warn('Failed to parse distribution plan:', parseResult.error)
          throw new Error('Invalid distribution plan format')
        }

        return parseResult.data
      } catch (error) {
        const parsedError = parseRequestError(error)
        toast({
          title: 'Preview Error',
          variant: 'destructive',
          description:
            parsedError.message ||
            'Unable to load distribution preview. Please try again.',
        })
        throw error
      }
    },
    enabled: open,
    retry: (failureCount, error) => {
      // Don't retry if it's a parsing error
      if (error?.message === 'Invalid distribution plan format') {
        return false
      }
      return failureCount < 3
    },
  })

  const downloadMutation = useDownloadDistributionPlanPreviewReport({
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `distribution-plan-preview-${programId}-${purse.id}-${new Date().toISOString().split('T')[0]}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
      }, 1000)

      toast({
        title: 'Award preview report downloaded',
        variant: 'default',
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Failed to download award report',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const handleDownload = () => {
    downloadMutation.mutate({
      programId,
      purseId: purse.id,
      distributionStrategy: 'single',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="h-4 w-4 text-blue-600" />
            Distribution Preview - {purse.name}
          </DialogTitle>
        </DialogHeader>

        <PageLoader query={previewQuery}>
          {(distributionPlan) => {
            const distributions =
              distributionPlan.summary.distributions_by_category ?? []

            const warnings = distributionPlan.warnings ?? null
            const duplicateEntries = warnings?.duplicates
              ? Object.entries(warnings.duplicates).filter(
                  ([, nums]) => nums.length > 0
                )
              : []
            const missingEligibilityEntries =
              warnings?.missing_purse_eligibility
                ? Object.entries(warnings.missing_purse_eligibility)
                : []
            const hasDuplicates =
              distributionPlan.has_duplicate_warning &&
              duplicateEntries.length > 0

            const sequenceGapEntries = warnings?.sequence_gaps
              ? Object.entries(warnings.sequence_gaps).filter(
                  ([, nums]) => nums.length > 0
                )
              : []

            return (
              <div className="space-y-4">
                {/* Warning Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Eye className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs w-full">
                      <p className="font-medium text-blue-800 mb-1">
                        Distribution Preview
                      </p>
                      <p className="text-blue-700">
                        This preview shows how scholarships will be distributed
                        if you proceed with the award process. Review the
                        details carefully before confirming.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Distribution Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    Distribution Summary
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">
                        {currencyFormatter.format(
                          distributionPlan.total_amount
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        Total Amount to be Distributed
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">
                        {distributionPlan.total_students}
                      </div>
                      <div className="text-xs text-gray-600">
                        Students to be Awarded
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">
                        {currencyFormatter.format(purse.balance_available)}
                      </div>
                      <div className="text-xs text-gray-600">
                        Available in Purse
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Check */}
                {distributionPlan.total_amount > purse.balance_available && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2 w-full">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium text-red-800 mb-1">
                          Insufficient Funds
                        </p>
                        <p className="text-red-700">
                          The total distribution amount (
                          {currencyFormatter.format(
                            distributionPlan.total_amount
                          )}
                          ) exceeds the available balance in this purse (
                          {currencyFormatter.format(purse.balance_available)}).
                          Please review the grade distributions or add more
                          funds to the purse.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {hasDuplicates && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs w-full">
                        <p className="font-medium text-red-800 mb-1">
                          Duplicate Sequence Numbers Detected
                        </p>
                        <p className="text-red-700 mb-2">
                          Resolve these duplicates before proceeding. Awarding
                          is disabled until they are resolved.
                        </p>
                        <ul className="text-red-700 space-y-1 list-disc pl-4">
                          {duplicateEntries.map(([priorityGroup, numbers]) => (
                            <li key={priorityGroup}>
                              Priority Group {priorityGroup} - Duplicate
                              Sequence Numbers: {numbers.join(', ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {sequenceGapEntries.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium text-amber-800 mb-1">
                          Sequence Number Gaps Detected
                        </p>
                        <p className="text-amber-700 mb-2">
                          Some sequence numbers are missing. This may affect the
                          distribution order but will not prevent awarding.
                        </p>
                        <ul className="text-amber-700 space-y-1 list-disc pl-4">
                          {sequenceGapEntries.map(
                            ([priorityGroup, numbers]) => (
                              <li key={priorityGroup}>
                                Priority Group {priorityGroup} - Missing
                                Sequence Numbers: {numbers.join(', ')}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {missingEligibilityEntries.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs w-full">
                        <p className="font-medium text-amber-800 mb-1">
                          Missing Purse Eligibility
                        </p>
                        <p className="text-amber-700 mb-4">
                          These students are not eligible for this purse. They
                          will not be awarded unless eligibility is added.
                        </p>
                        <div className="max-h-48 overflow-y-auto pr-1">
                          <ul className="text-amber-700 space-y-1 list-disc pl-4">
                            {missingEligibilityEntries.map(
                              ([studentId, info]) => (
                                <li key={studentId}>
                                  Student ID {studentId} —{' '}
                                  {info.name ?? 'Name Unavailable'}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Distribution Breakdown */}
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-3">
                    Distribution Breakdown by {purse.awarding_method_name}
                  </h3>
                  <div className="space-y-2">
                    {distributions.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white border rounded-lg p-3"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {item.name}
                            </div>
                            <div className="text-xs text-gray-600">
                              {item.student_count} student
                              {item.student_count !== 1 ? 's' : ''} eligible
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900 text-sm">
                              {currencyFormatter.format(
                                item.amount_per_student
                              )}{' '}
                              per student
                            </div>
                            <div className="text-xs text-gray-600">
                              Total: {currencyFormatter.format(item.allocation)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <p className="font-medium text-amber-800 mb-1">
                        Important Notes
                      </p>
                      <ul className="text-amber-700 space-y-0.5">
                        <li>
                          • Students will be selected based on their sequence
                          number (lowest first)
                        </li>
                        <li>
                          • This action cannot be undone once the distribution
                          process starts
                        </li>
                        <li>
                          • You'll receive a notification when the distribution
                          is complete
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )
          }}
        </PageLoader>

        <DialogFooter className="flex justify-between">
          <SubmitButton
            type="button"
            variant="outline"
            size="sm"
            status={downloadMutation.status}
            buttonTxt="Download Award Report"
            submittingText="Downloading..."
            className="justify-start mr-auto w-auto"
            onClick={handleDownload}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="sm"
          >
            Close Preview
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={onProceedToAward}
              disabled={
                previewQuery.isLoading ||
                previewQuery.isError ||
                !previewQuery.data ||
                previewQuery.data.total_amount > purse.balance_available ||
                previewQuery.data.has_duplicate_warning
              }
              className="gap-2"
              size="sm"
            >
              <Award className="h-4 w-4" />
              Proceed to Award
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
